import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const UserPreferencesContext = createContext();

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return context;
};

// eslint-disable-next-line max-lines-per-function
export const UserPreferencesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadSettings();
    } else {
      setSettings(null);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUserSettings();
      setSettings(data);
    } catch (err) {
      console.error('Failed to load user settings:', err);
      setError(err.message);
      // Use default settings on error
      setSettings(getDefaultSettings());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSettings = () => ({
    voice: {
      enabled: false,
      wakeWord: 'hey coach',
      language: 'en-US',
      tts: {
        enabled: false,
        voice: 'Google US English',
        speed: 1.0,
        pitch: 1.0,
        volume: 1.0,
      },
      behavior: {
        accumulationMode: true,
        autoSend: false,
        confirmBeforeSend: false,
        feedbackSounds: true,
        continuousListening: true,
        bypassDuration: 15,
      },
      advanced: {
        adaptiveBattery: true,
        noiseSupression: true,
        echoCancellation: true,
      }
    },
    display: {
      theme: 'light',
      density: 'comfortable',
      animations: true,
    },
    notifications: {
      email: true,
      browser: true,
      reportComplete: true,
      dailyDigest: false,
    }
  });

  const updateSettings = async (updates) => {
    try {
      const updated = await api.updateUserSettings(updates);
      setSettings(updated);
      return updated;
    } catch (err) {
      console.error('Failed to update settings:', err);
      throw err;
    }
  };

  const updateSettingsCategory = async (category, categorySettings) => {
    try {
      const updated = await api.updateUserSettingsCategory(category, categorySettings);
      setSettings(updated);
      return updated;
    } catch (err) {
      console.error('Failed to update settings category:', err);
      throw err;
    }
  };

  const resetSettings = async () => {
    try {
      const reset = await api.resetUserSettings();
      setSettings(reset);
      return reset;
    } catch (err) {
      console.error('Failed to reset settings:', err);
      throw err;
    }
  };

  return (
    <UserPreferencesContext.Provider value={{
      settings,
      loading,
      error,
      updateSettings,
      updateSettingsCategory,
      resetSettings,
      reloadSettings: loadSettings
    }}>
      {children}
    </UserPreferencesContext.Provider>
  );
};